import React, { useState } from "react";
import Modal from "./Modal";
import PhoneInput, { phoneDigitsValid } from "./PhoneInput";
import { api } from "../lib/api";
import { toast } from "sonner";
import ContactoAdicional, { CONTACTO_VACIO } from "./ContactoAdicional";

const EMPTY_FORM = { name: "", phone: "", birth_date: "", email: "", source: "WhatsApp", interest: "", notes: "", ...CONTACTO_VACIO };

export default function NewPatientModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.name) {
      toast.error("El nombre es obligatorio");
      return;
    }
    if (!phoneDigitsValid(form.phone, true)) {
      toast.error("El teléfono debe tener 10 dígitos");
      return;
    }
    if (!form.birth_date) {
      toast.error("La fecha de nacimiento es obligatoria");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/patients", form);
      toast.success(`Paciente ${data.name} registrado`);
      onCreated?.(data);
      onClose();
      setForm(EMPTY_FORM);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo registrar el paciente");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Registrar paciente"
      testid="modal-new-patient"
      footer={
        <>
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-slate-300 hover:bg-slate-50" data-testid="patient-cancel">
            Cancelar
          </button>
          <button onClick={submit} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-brand-electric text-white hover:bg-blue-700 disabled:opacity-60" data-testid="patient-submit">
            {saving ? "Guardando…" : "Registrar paciente"}
          </button>
        </>
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Nombre completo">
          <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="patient-name" />
        </Field>
        <Field label="Teléfono">
          <PhoneInput value={form.phone} onChange={(digits) => setForm({ ...form, phone: digits })} required testId="patient-phone" />
        </Field>
        <Field label="Fecha de nacimiento">
          <input
            type="date"
            className="input"
            max={new Date().toISOString().slice(0, 10)}
            value={form.birth_date}
            onChange={(e) => setForm({ ...form, birth_date: e.target.value })}
            data-testid="patient-birth-date"
          />
        </Field>
        <Field label="Email">
          <input className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="patient-email" />
        </Field>
        <Field label="Fuente">
          <select className="input" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} data-testid="patient-source">
            {["WhatsApp", "Instagram", "Facebook", "Google", "Referencia", "Otro"].map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </Field>
        <div className="sm:col-span-2">
          <Field label="Interés">
            <input className="input" value={form.interest} onChange={(e) => setForm({ ...form, interest: e.target.value })} placeholder="Valoración inicial, limpieza, etc." data-testid="patient-interest" />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Notas">
            <textarea rows={2} className="input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="patient-notes" />
          </Field>
        </div>
      </div>
      <style>{`.input { width:100%; border:1px solid #cbd5e1; background:#fff; border-radius:8px; padding:.55rem .75rem; font-size:.875rem; outline:none; } .input:focus { border-color:#2563EB; box-shadow:0 0 0 2px rgba(37,99,235,.18); }`}</style>
      <div className="mt-4"><ContactoAdicional value={form} onChange={setForm} /></div>
    </Modal>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
